/* ==========================================================================
   Lalla — theme.js
   Lightweight global utilities (no dependencies):
   helpers, money formatting, live announcements, pub/sub, scroll lock,
   focus trap, section rendering, <modal-dialog>, <quantity-input>,
   French form validation and scroll reveal.
   ========================================================================== */

(() => {
  'use strict';

  const Lalla = (window.Lalla = window.Lalla || {});
  const strings = Lalla.strings || {};

  /* ------------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------------ */

  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => Array.from(context.querySelectorAll(selector));

  const debounce = (fn, wait = 250) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const fetchConfig = (type = 'json') => ({
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/${type}`,
      'X-Requested-With': 'XMLHttpRequest',
    },
  });

  /* ------------------------------------------------------------------------
     Money — mirrors the placeholders supported by Shopify's money formats.
     Server-rendered prices (Liquid money filters) remain the source of truth;
     this is only used for client-side totals (cart drawer, sticky bar).
     ------------------------------------------------------------------------ */

  function formatMoney(cents, format = Lalla.moneyFormat || '{{amount}}') {
    const value = typeof cents === 'string' ? Number(cents.replace(/[^\d-]/g, '')) : Number(cents);
    if (!Number.isFinite(value)) return '';

    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(pattern);
    if (!match) return format;

    const formatNumber = (number, precision, thousands = ',', decimal = '.') => {
      const [integer, fraction] = (number / 100).toFixed(precision).split('.');
      const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };

    const formatters = {
      amount: () => formatNumber(value, 2),
      amount_no_decimals: () => formatNumber(value, 0),
      amount_with_comma_separator: () => formatNumber(value, 2, '.', ','),
      amount_no_decimals_with_comma_separator: () => formatNumber(value, 0, '.', ','),
      amount_with_apostrophe_separator: () => formatNumber(value, 2, "'", '.'),
      amount_no_decimals_with_space_separator: () => formatNumber(value, 0, ' '),
      amount_with_space_separator: () => formatNumber(value, 2, ' ', ','),
      amount_with_period_and_space_separator: () => formatNumber(value, 2, ' ', '.'),
    };

    const formatted = (formatters[match[1]] || formatters.amount)();
    return format.replace(pattern, formatted).replace(/<[^>]*>/g, '');
  }

  /* ------------------------------------------------------------------------
     Screen-reader announcements
     ------------------------------------------------------------------------ */

  function announce(message, { assertive = false } = {}) {
    const region = document.getElementById(assertive ? 'A11yAlert' : 'A11yStatus');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 80);
  }

  /* ------------------------------------------------------------------------
     Pub/sub — e.g. publish('cart:update', { cart, source })
     Also dispatched as DOM events ("lalla:cart:update") for third-party apps.
     ------------------------------------------------------------------------ */

  const subscribers = {};

  function subscribe(eventName, callback) {
    (subscribers[eventName] = subscribers[eventName] || []).push(callback);
    return () => {
      subscribers[eventName] = subscribers[eventName].filter((cb) => cb !== callback);
    };
  }

  function publish(eventName, data) {
    (subscribers[eventName] || []).forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(error);
      }
    });
    document.dispatchEvent(new CustomEvent(`lalla:${eventName}`, { detail: data }));
  }

  /* ------------------------------------------------------------------------
     Scroll lock (counts nested locks, compensates the scrollbar width)
     ------------------------------------------------------------------------ */

  let scrollLocks = 0;

  function lockScroll() {
    if (scrollLocks++ > 0) return;
    const gap = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--scrollbar-gap', `${Math.max(gap, 0)}px`);
    document.documentElement.classList.add('scroll-locked');
  }

  function unlockScroll() {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks > 0) return;
    document.documentElement.classList.remove('scroll-locked');
    document.documentElement.style.removeProperty('--scrollbar-gap');
  }

  /* ------------------------------------------------------------------------
     Focus management for non-dialog containers (e.g. mega menu panels).
     Native <dialog>.showModal() handles focus containment by itself.
     ------------------------------------------------------------------------ */

  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

  const getFocusable = (container) =>
    qsa(FOCUSABLE, container).filter((el) => el.getClientRects().length > 0 && !el.closest('[inert]'));

  let activeTrap = null;

  function removeTrapFocus(elementToFocus) {
    if (activeTrap) {
      document.removeEventListener('keydown', activeTrap);
      activeTrap = null;
    }
    if (elementToFocus && typeof elementToFocus.focus === 'function') elementToFocus.focus();
  }

  function trapFocus(container, elementToFocus) {
    removeTrapFocus();
    activeTrap = (event) => {
      if (event.key !== 'Tab') return;
      const focusable = getFocusable(container);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', activeTrap);
    const target = elementToFocus || getFocusable(container)[0] || container;
    target.focus();
  }

  /* ------------------------------------------------------------------------
     Section Rendering API helper
     ------------------------------------------------------------------------ */

  async function fetchSection(sectionId, url = window.location.href) {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    const response = await fetch(target.toString(), { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`Section ${sectionId}: HTTP ${response.status}`);
    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  }

  /* ------------------------------------------------------------------------
     <modal-dialog> — wraps a native <dialog class="dialog">.
     Open with any element carrying data-modal-open="<modal-dialog id>".
     Close with [data-modal-close], Escape, or a backdrop click.
     ------------------------------------------------------------------------ */

  class ModalDialog extends HTMLElement {
    connectedCallback() {
      if (this.dialog) return;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;

      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });

      this.dialog.addEventListener('close', () => {
        unlockScroll();
        this.dispatchEvent(new CustomEvent('modal:close', { bubbles: true }));
        if (this.opener && document.contains(this.opener)) this.opener.focus({ preventScroll: true });
        this.opener = null;
      });

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-modal-close]')) {
          event.preventDefault();
          this.close();
        }
      });
    }

    get isOpen() {
      return Boolean(this.dialog && this.dialog.open);
    }

    open(opener) {
      if (!this.dialog || this.dialog.open) return;
      this.opener = opener || document.activeElement;
      this.dialog.showModal();
      lockScroll();
      const preferred = qs('[autofocus], [data-autofocus]', this.dialog);
      if (preferred) preferred.focus();
      this.dispatchEvent(new CustomEvent('modal:open', { bubbles: true }));
    }

    close() {
      if (this.dialog && this.dialog.open) this.dialog.close();
    }
  }

  if (!customElements.get('modal-dialog')) customElements.define('modal-dialog', ModalDialog);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-modal-open]');
    if (!trigger) return;
    const modal = document.getElementById(trigger.dataset.modalOpen);
    if (!modal || typeof modal.open !== 'function') return;
    event.preventDefault();
    modal.open(trigger);
  });

  /* ------------------------------------------------------------------------
     <quantity-input> — [-] <input type="number"> [+]
     Buttons use aria-disabled (not disabled) so focus is never lost.
     ------------------------------------------------------------------------ */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (this.input) return;
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.minus = this.querySelector('button[name="minus"]');
      this.plus = this.querySelector('button[name="plus"]');

      this.addEventListener('click', (event) => {
        const button = event.target.closest('button[name="minus"], button[name="plus"]');
        if (!button) return;
        event.preventDefault();
        if (button.getAttribute('aria-disabled') === 'true') return;
        const previous = this.input.value;
        if (button.name === 'plus') this.input.stepUp();
        else this.input.stepDown();
        if (previous !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      this.input.addEventListener('change', () => this.update(true));
      this.update(false);
    }

    /* Values are only corrected after customer input, never on load
       (the server-rendered quantity is the truth). */
    update(fromUser = false) {
      const value = parseInt(this.input.value, 10);
      const min = parseInt(this.input.min || '0', 10);
      const max = this.input.max ? parseInt(this.input.max, 10) : null;
      if (fromUser && (Number.isNaN(value) || value < min)) this.input.value = String(min);
      if (fromUser && max !== null && value > max) {
        this.input.value = String(max);
        // Never correct silently: listeners (e.g. the cart) tell the customer why.
        this.dispatchEvent(new CustomEvent('quantity:clamped', { bubbles: true, detail: { max, input: this.input } }));
      }
      const current = parseInt(this.input.value, 10);
      if (this.minus) this.minus.setAttribute('aria-disabled', String(current <= min));
      if (this.plus) this.plus.setAttribute('aria-disabled', String(max !== null && current >= max));
    }
  }

  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------------------------
     Form validation with French messages.
     Add data-validate + novalidate to a <form>; each field needs an
     element with id="<field-id>-error" (rendered hidden) for its message.
     ------------------------------------------------------------------------ */

  const validationStrings = strings.validation || {};

  function messageFor(field) {
    const v = field.validity;
    if (v.valueMissing) return field.dataset.msgRequired || validationStrings.required;
    if (v.typeMismatch && field.type === 'email') return validationStrings.email;
    if (v.typeMismatch) return validationStrings.invalid;
    if (v.patternMismatch) return field.dataset.msgPattern || validationStrings.pattern;
    if (v.tooShort) return (validationStrings.too_short || '').replace('[count]', field.minLength);
    if (v.tooLong) return (validationStrings.too_long || '').replace('[count]', field.maxLength);
    if (v.rangeUnderflow || v.rangeOverflow || v.stepMismatch) return validationStrings.invalid;
    if (v.customError) return field.validationMessage;
    return validationStrings.invalid;
  }

  function setFieldError(field, message) {
    const errorEl = document.getElementById(`${field.id}-error`);
    field.setAttribute('aria-invalid', 'true');
    if (!errorEl) return;
    const textEl = qs('[data-error-text]', errorEl) || errorEl;
    textEl.textContent = message;
    errorEl.hidden = false;
    const describedBy = new Set((field.getAttribute('aria-describedby') || '').split(' ').filter(Boolean));
    describedBy.add(errorEl.id);
    field.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
  }

  function clearFieldError(field) {
    const errorEl = document.getElementById(`${field.id}-error`);
    field.removeAttribute('aria-invalid');
    if (errorEl) errorEl.hidden = true;
  }

  function validateField(field) {
    if (field.checkValidity()) {
      clearFieldError(field);
      return true;
    }
    setFieldError(field, messageFor(field));
    return false;
  }

  function validateForm(form) {
    const fields = qsa('input, select, textarea', form).filter(
      (field) => field.willValidate && field.type !== 'hidden' && field.type !== 'submit'
    );
    const invalid = fields.filter((field) => !validateField(field));
    if (invalid.length) {
      invalid[0].focus();
      announce(validationStrings.form_errors || '', { assertive: true });
      return false;
    }
    return true;
  }

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-validate')) return;
      // A button with formnovalidate (e.g. "Refuser l'invitation") skips validation.
      if (event.submitter && event.submitter.formNoValidate) return;
      if (!validateForm(form)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  // Re-validate a field the customer is correcting.
  document.addEventListener('input', (event) => {
    const field = event.target;
    if (field.getAttribute && field.getAttribute('aria-invalid') === 'true' && field.form && field.form.hasAttribute('data-validate')) {
      validateField(field);
    }
  });

  /* ------------------------------------------------------------------------
     Scroll reveal — elements with .reveal fade/slide in once.
     Children of [data-reveal-group] are staggered.
     ------------------------------------------------------------------------ */

  let revealObserver = null;

  function initReveal(root = document) {
    const elements = qsa('.reveal:not(.is-visible)', root);
    if (!elements.length) return;

    const animationsOn = document.documentElement.dataset.animations === 'true';
    if (!animationsOn || prefersReducedMotion() || !('IntersectionObserver' in window)) {
      elements.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    qsa('[data-reveal-group]', root).forEach((group) => {
      qsa('.reveal', group).forEach((el, index) => el.style.setProperty('--reveal-index', String(index % 6)));
    });

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
      );
    }

    elements.forEach((el) => revealObserver.observe(el));
  }

  /* ------------------------------------------------------------------------
     Keep --header-height in sync with the sticky header (if present).
     ------------------------------------------------------------------------ */

  function observeHeaderHeight() {
    const header = qs('[data-header-root]');
    if (!header || !('ResizeObserver' in window)) return;
    const setHeight = () => {
      document.documentElement.style.setProperty('--header-height', `${Math.round(header.getBoundingClientRect().height)}px`);
    };
    new ResizeObserver(setHeight).observe(header);
    setHeight();
  }

  /* ------------------------------------------------------------------------
     Cart count (header badge + accessible label), driven by cart:update.
     ------------------------------------------------------------------------ */

  function updateCartCount(count) {
    qsa('[data-cart-count]').forEach((el) => {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
    let labels = null;
    try {
      labels = JSON.parse(qs('[data-cart-count-strings]')?.textContent || 'null');
    } catch (error) {
      labels = null;
    }
    if (!labels) return;
    const detail = count === 0 ? labels.empty : (count === 1 ? labels.one : labels.other).replace('[count]', count);
    qsa('[data-cart-trigger]').forEach((link) => link.setAttribute('aria-label', `${labels.label}, ${detail}`));
  }

  subscribe('cart:update', (detail) => {
    if (detail && detail.cart && typeof detail.cart.item_count === 'number') updateCartCount(detail.cart.item_count);
  });

  /* ------------------------------------------------------------------------
     <copy-button data-copy="…" data-copied="Lien copié"> wrapping a <button>.
     Copies the value (or the current URL when data-copy is empty) and
     confirms in place and through the live region. If the clipboard is not
     available, the value is shown selected so it can be copied by hand.
     ------------------------------------------------------------------------ */

  class CopyButton extends HTMLElement {
    connectedCallback() {
      this.button = qs('button', this);
      if (!this.button || this.button.dataset.bound) return;
      this.button.dataset.bound = 'true';
      this.label = qs('[data-copy-label]', this) || this.button;
      this.original = this.label.textContent;
      this.button.addEventListener('click', () => this.copy());
    }

    async copy() {
      const value = this.dataset.copy || window.location.href;
      try {
        if (!navigator.clipboard || !window.isSecureContext) throw new Error('clipboard unavailable');
        await navigator.clipboard.writeText(value);
        this.confirm();
      } catch (error) {
        this.fallback(value);
      }
    }

    confirm() {
      const message = this.dataset.copied || '';
      this.label.textContent = message || this.original;
      this.button.classList.add('is-copied');
      announce(message);
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => {
        this.label.textContent = this.original;
        this.button.classList.remove('is-copied');
      }, 2500);
    }

    fallback(value) {
      let field = qs('[data-copy-fallback]', this);
      if (!field) {
        field = document.createElement('input');
        field.type = 'text';
        field.readOnly = true;
        field.className = 'field__input copy-button__fallback';
        field.setAttribute('data-copy-fallback', '');
        field.setAttribute('aria-label', this.original.trim());
        this.appendChild(field);
      }
      field.value = value;
      field.focus();
      field.select();
    }
  }

  if (!customElements.get('copy-button')) customElements.define('copy-button', CopyButton);

  /* ------------------------------------------------------------------------
     Init
     ------------------------------------------------------------------------ */

  Object.assign(Lalla, {
    qs,
    qsa,
    debounce,
    fetchConfig,
    formatMoney,
    announce,
    subscribe,
    publish,
    lockScroll,
    unlockScroll,
    trapFocus,
    removeTrapFocus,
    getFocusable,
    fetchSection,
    validateField,
    validateForm,
    setFieldError,
    clearFieldError,
    initReveal,
    updateCartCount,
    prefersReducedMotion,
    ready: true,
  });

  const init = () => {
    initReveal();
    observeHeaderHeight();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor: re-initialise content injected by section reloads.
  document.addEventListener('shopify:section:load', (event) => {
    initReveal(event.target);
    if (qs('[data-header-root]', event.target)) observeHeaderHeight();
  });
})();
